import {useQuery} from "@apollo/client";
import {format} from "date-fns";
import {useCallback, useMemo} from "react";
import {type DateRange} from "react-day-picker";
import {Helmet} from "react-helmet-async";
import {FiArrowLeft} from "react-icons/fi";
import {useNavigate, useParams, useSearchParams} from "react-router";

import {Button} from "@/components/button/Button.tsx";
import {DatePicker} from "@/components/datePicker/DatePicker.tsx";
import {useDateRange} from "@/components/datePicker/DatePickerURLRangeParamsContext.ts";
import {Loader} from "@/components/placeholder/Loader.tsx";
import {
  GQL_PRIVATE_COMPONENT_DRILLDOWN_QUERY,
  GQL_PRIVATE_STATUSPAGE_QUERY,
  GQL_PUBLIC_COMPONENT_DRILLDOWN_QUERY,
  GQL_PUBLIC_STATUSPAGE_QUERY,
} from "@/lib/queries/statusPageConsumption.ts";

import {DrilldownComponentHeader} from "../components/drilldown/DrilldownComponentHeader.tsx";
import {DrilldownKPICards} from "../components/drilldown/DrilldownKPICards.tsx";
import {DrilldownResponseTimeChart} from "../components/drilldown/DrilldownResponseTimeChart.tsx";
import {DrilldownUptimeBar} from "../components/drilldown/DrilldownUptimeBar.tsx";
import {StatusPageFooter} from "../components/StatusPageFooter.tsx";
import {ConsumptionNotFound} from "../ConsumptionNotFound.tsx";
import {ConsumptionPageLoader} from "../ConsumptionPageLoader.tsx";
import {useFavicon} from "../hooks/useFavicon.ts";
import {InspireHeader} from "../themes/inspire/InspireHeader.tsx";
import type {ComponentDrilldownResult, PublicStatusPage} from "../types.ts";
import {DATE_RANGE_END_PARAM, DATE_RANGE_START_PARAM, defaultDateRangeForDays} from "../utils/dateRange.ts";

import pageStyles from "../StatusPageConsumption.module.scss";
import styles from "./CheckDrilldown.module.scss";

interface CheckDrilldownProps {
  mode?: "public" | "private";
  // Set by HostCheckDrilldown when reached via a custom domain: back-navigation stays on
  // the clean root instead of "/{slug}".
  hostMode?: boolean;
  // Host-resolved slug (custom domain, no slug in the path); overrides the URL param.
  slug?: string;
}

export const CheckDrilldown = ({mode = "public", hostMode = false, slug: slugOverride}: CheckDrilldownProps) => {
  // The drill-down is addressed by check id (not component id) so the URL matches the legacy
  // component page and previously shared links keep resolving to the same component.
  const {slug: slugParam, id, serviceId} = useParams<{slug?: string; id?: string; serviceId: string}>();
  // On a custom domain the slug is resolved from the host and passed in, not present in the URL.
  const slug = slugOverride ?? slugParam;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isPrivate = mode === "private";
  const parsedId = id ? parseInt(id, 10) : undefined;
  const parsedServiceId = serviceId ? parseInt(serviceId, 10) : undefined;

  const isDark = searchParams.get("theme") === "dark";

  // Fetch status page for metadata (theme, name, etc.)
  const {data: publicPageData, loading: publicPageLoading} = useQuery<{publicStatusPage: PublicStatusPage | null}>(
    GQL_PUBLIC_STATUSPAGE_QUERY,
    {
      variables: {slug: slug ?? ""},
      skip: isPrivate || !slug,
      context: {statusPageSlug: slug, skipAuthError: true},
    }
  );

  const {data: privatePageData, loading: privatePageLoading} = useQuery<{
    privateStatusPage: PublicStatusPage | null;
  }>(GQL_PRIVATE_STATUSPAGE_QUERY, {
    variables: {id: parsedId ?? 0},
    skip: !isPrivate || !parsedId,
  });

  const statusPage = isPrivate
    ? (privatePageData?.privateStatusPage ?? null)
    : (publicPageData?.publicStatusPage ?? null);
  const statusPageLoading = isPrivate ? privatePageLoading : publicPageLoading;
  useFavicon(statusPage?.favicon_url);

  const handleToggleDarkMode = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (isDark) {
        next.delete("theme");
      } else {
        next.set("theme", "dark");
      }
      return next;
    });
  }, [isDark, setSearchParams]);

  const pageTimezone = statusPage?.used_timezone;
  const defaultDays = statusPage?.default_status_date_range || 7;
  // Memoized because useDateRange re-resolves whenever this changes identity.
  const withDefault = useMemo(() => {
    const range = defaultDateRangeForDays(defaultDays, pageTimezone);
    return () => range;
  }, [defaultDays, pageTimezone]);

  // The range lives in the URL so it can be shared, and falls back to the page's own setting. Day
  // boundaries are resolved in the status page's timezone, not the viewer's: the backend resolves
  // the requested days that way, and snapping in browser-local time leaves the bar's window
  // straddling the returned one, whose uncovered edge renders as no-data.
  const {dateRange, fixedRangeOption, handleRangeChange} = useDateRange({
    fromKey: DATE_RANGE_START_PARAM,
    toKey: DATE_RANGE_END_PARAM,
    fixedRangeKey: "range",
    withDefault,
    timezone: pageTimezone,
    paramFormat: "legacyDays",
    navigateOptions: {replace: true, preventScrollReset: true},
    normalizeSelection: true,
    deriveFixedRange: true,
  });
  const rangeStart = dateRange.from;
  const rangeEnd = dateRange.to;

  const startStr = format(rangeStart, "yyyyMMdd");
  const endStr = format(rangeEnd, "yyyyMMdd");

  const {data: publicDrilldownData, loading: publicLoading} = useQuery<{
    publicComponentDrilldown: ComponentDrilldownResult | null;
  }>(GQL_PUBLIC_COMPONENT_DRILLDOWN_QUERY, {
    variables: {slug: slug ?? "", serviceId: parsedServiceId ?? 0, startDate: startStr, endDate: endStr},
    skip: isPrivate || !slug || !parsedServiceId,
    context: {statusPageSlug: slug, skipAuthError: true},
  });

  const {data: privateDrilldownData, loading: privateLoading} = useQuery<{
    privateComponentDrilldown: ComponentDrilldownResult | null;
  }>(GQL_PRIVATE_COMPONENT_DRILLDOWN_QUERY, {
    variables: {id: parsedId ?? 0, serviceId: parsedServiceId ?? 0, startDate: startStr, endDate: endStr},
    skip: !isPrivate || !parsedId || !parsedServiceId,
  });

  const drilldownLoading = isPrivate ? privateLoading : publicLoading;
  const drilldown = isPrivate
    ? (privateDrilldownData?.privateComponentDrilldown ?? null)
    : (publicDrilldownData?.publicComponentDrilldown ?? null);

  // Navigate to the parent status page route explicitly rather than navigate(-1): the drilldown
  // can be opened directly (new tab / shared link) where there is no in-app history to pop, and
  // relying on history can leave the SPA and trigger a full document reload. The target is resolved
  // by dropping the component segment from the current path, so it holds both at the SPA root
  // (status-page domains) and under the `/status-page` prefix in the main app.
  // The query string carries the theme and the date range, both of which the status page behind
  // this one shares — rebuilding it here instead of carrying it dropped the visitor's range.
  const handleBack = useCallback(() => {
    // Host mode has no slug segment (the route is "/c/{serviceId}"), so ".." would land on
    // "/c" (a bogus :slug); send it to the clean root instead. Path/private modes drop the
    // trailing component segment as before.
    if (hostMode) {
      navigate({pathname: "/", search: searchParams.toString()});
      return;
    }
    navigate({pathname: "..", search: searchParams.toString()}, {relative: "path"});
  }, [hostMode, navigate, searchParams]);

  // A half-open selection is the picker mid-interaction, not a range to render.
  const handleDateChange = (value: DateRange | undefined) => {
    if (value?.from && value?.to) {
      handleRangeChange({from: value.from, to: value.to});
    }
  };

  const handleRefresh = useCallback(() => {}, []);

  if (statusPageLoading && !statusPage) {
    return <ConsumptionPageLoader />;
  }

  // Not loading and still no page (a bad slug, or no slug at all when the host didn't resolve):
  // say so instead of spinning forever.
  if (!statusPage) {
    return <ConsumptionNotFound />;
  }

  if (!drilldownLoading && !drilldown) {
    return (
      <ConsumptionNotFound
        title="Component Not Found"
        message="The component you are looking for does not exist or drill-down is not enabled."
      />
    );
  }

  return (
    <div className={pageStyles.pageWrapper} data-theme={isDark ? "dark" : "light"}>
      <Helmet>
        <title>{drilldown ? `${drilldown.component_name} — ${statusPage.name}` : statusPage.name}</title>
        {/* Check-analysis drill-down pages are never indexable (legacy parity). */}
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <InspireHeader
        statusPage={statusPage}
        slug={slug ?? ""}
        isDark={isDark}
        onToggleDarkMode={handleToggleDarkMode}
        onRefresh={handleRefresh}
        autoRefreshPageKey={undefined}
        showOverallStatus={false}
      />

      <main className={styles.body}>
        <div className={styles.container}>
          <div className={styles.topRow}>
            <Button variant="linkBlue" size="fitContent" onClick={handleBack} className={styles.backButton}>
              <FiArrowLeft size={16} />
              <span>Back To Status Page</span>
            </Button>
            <div className={styles.datePickerWrapper}>
              <DatePicker
                mode="range"
                readOnly
                selected={{from: rangeStart, to: rangeEnd}}
                fixedRange={fixedRangeOption?.value}
                onChange={handleDateChange}
                timezone={pageTimezone ?? "UTC"}
                placement="bottom-end"
                disableFutureDates
              />
            </div>
          </div>

          <div className={styles.contentFrame}>
            <div className={styles.contentCard}>
              {drilldownLoading || !drilldown ? (
                <Loader />
              ) : (
                <>
                  <DrilldownComponentHeader drilldown={drilldown} />
                  <DrilldownKPICards
                    drilldown={drilldown}
                    timezone={statusPage.used_timezone}
                    timeFormat={statusPage.time_format}
                  />
                  <DrilldownUptimeBar
                    drilldown={drilldown}
                    start={rangeStart}
                    end={rangeEnd}
                    timezone={statusPage.used_timezone}
                    timeFormat={statusPage.time_format}
                    components={statusPage.components}
                    statusMetadata={statusPage.component_status_metadata}
                    isDark={isDark}
                  />
                  <DrilldownResponseTimeChart
                    drilldown={drilldown}
                    start={rangeStart}
                    end={rangeEnd}
                    timezone={statusPage.used_timezone}
                    isDark={isDark}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </main>

      {statusPage.show_branding_footer && <StatusPageFooter />}
    </div>
  );
};
